/**
 * ReportesPage — Consumo real por embarcación por día en un rango.
 * Fórmula: consumo = nivel_am + cargas_del_dia - nivel_pm
 * Solo operaciones/admin/superadmin.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, CircularProgress, Chip, Grid, MenuItem,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const rangoInicial = () => {
  const hasta = new Date();
  const desde = new Date(); desde.setDate(desde.getDate() - 7);
  const iso = d => d.toISOString().slice(0, 10);
  return { desde: iso(desde), hasta: iso(hasta) };
};

const NIVEL_COLOR = {
  rojo: { bg: '#fee2e2', fg: '#991b1b', border: '#dc2626' },
  amarillo: { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}`;
};

export default function ReportesPage() {
  const empresaId = getEmpresaId();
  const [rango, setRango] = useState(rangoInicial);
  const [data, setData] = useState(null);
  const [anomalias, setAnomalias] = useState(null);
  const [gastoMensual, setGastoMensual] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [r, rA, rG] = await Promise.all([
        fetch(`${API_BASE}/turismo/combustible/reportes/consumo-diario?empresa_id=${empresaId}&fecha_desde=${rango.desde}&fecha_hasta=${rango.hasta}`,
          { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/combustible/reportes/anomalias?empresa_id=${empresaId}&dias=30`,
          { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/combustible/reportes/gasto-mensual?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }),
      ]);
      if (r.ok) setData(await r.json());
      if (rA.ok) setAnomalias(await rA.json());
      if (rG.ok) setGastoMensual(await rG.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId, rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const setR = (k) => (e) => setRango(p => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: '#dc2626' }} />
            Consumo diario
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Consumo real = nivel_AM + cargas del día − nivel_PM
          </Typography>
        </Box>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={rango.desde} onChange={setR('desde')} sx={{ width: 160 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={rango.hasta} onChange={setR('hasta')} sx={{ width: 160 }} />
      </Box>

      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#dc2626' }} />
        </Box>
      ) : (
        <>
          {/* Gasto mensual — últimos 6 meses */}
          {gastoMensual && (gastoMensual.por_mes?.length || 0) > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#059669', mb: 1.5, textTransform: 'uppercase' }}>
                💰 Gasto mensual · últimos 6 meses
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                    Total galones
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                    {Number(gastoMensual.totales.galones).toLocaleString('es-CO', { maximumFractionDigits: 1 })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                    Total pagado
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#059669' }}>
                    ${Number(gastoMensual.totales.pagado).toLocaleString('es-CO')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                    Cargas
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                    {gastoMensual.totales.cargas}
                  </Typography>
                </Grid>
              </Grid>
              {/* Serie mensual */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {gastoMensual.por_mes.map(m => {
                  const maxPag = Math.max(...gastoMensual.por_mes.map(x => x.pagado), 1);
                  const altura = Math.max(8, Math.round((m.pagado / maxPag) * 80));
                  return (
                    <Box key={m.mes} sx={{ flex: 1, minWidth: 80, textAlign: 'center' }}>
                      <Box sx={{
                        height: 100, display: 'flex', alignItems: 'flex-end',
                        justifyContent: 'center', mb: 0.5,
                      }}>
                        <Box sx={{
                          width: '80%', height: altura,
                          background: 'linear-gradient(180deg, #16a34a 0%, #15803d 100%)',
                          borderRadius: 1,
                        }} title={`$${Number(m.pagado).toLocaleString('es-CO')}`} />
                      </Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>
                        {m.mes}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                        ${(m.pagado / 1000).toFixed(0)}k
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>
                        {Number(m.galones).toFixed(0)} gal
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {/* Top embarcaciones */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                Ranking por embarcación
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {gastoMensual.por_embarcacion.slice(0, 8).map(e => {
                  const max = gastoMensual.por_embarcacion[0]?.pagado || 1;
                  const pct = Math.round((e.pagado / max) * 100);
                  return (
                    <Box key={e.embarcacion_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 120 }}>
                        {e.nombre}
                      </Typography>
                      <Box sx={{ flex: 1, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#059669' }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, minWidth: 90, textAlign: 'right', color: '#059669', fontWeight: 700 }}>
                        ${Number(e.pagado).toLocaleString('es-CO')}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#94a3b8', minWidth: 60, textAlign: 'right' }}>
                        {Number(e.galones).toFixed(0)} gal
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}

          {/* Anomalías (últimos 30 días) */}
          {anomalias && ((anomalias.anomalias_consumo?.length || 0) + (anomalias.anomalias_precio?.length || 0)) > 0 && (
            <Paper sx={{ p: 2, mb: 3, border: '2px solid #fca5a5' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#991b1b', mb: 1.5, textTransform: 'uppercase' }}>
                ⚠️ Anomalías detectadas · últimos {anomalias.dias_analizados} días
              </Typography>
              {anomalias.anomalias_consumo?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626', mb: 0.5 }}>
                    Consumos anómalos ({anomalias.anomalias_consumo.length})
                  </Typography>
                  {anomalias.anomalias_consumo.slice(0, 5).map(a => {
                    const c = NIVEL_COLOR[a.nivel] || NIVEL_COLOR.amarillo;
                    return (
                      <Box key={a.consumo_id} sx={{
                        p: 1, mb: 0.5, borderRadius: 1, bgcolor: c.bg, border: `1px solid ${c.border}`,
                        display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                      }}>
                        <Chip label={`${a.desviacion_pct > 0 ? '+' : ''}${a.desviacion_pct}%`} size="small"
                          sx={{ bgcolor: c.border, color: '#fff', fontWeight: 700, fontSize: 11 }} />
                        <Typography sx={{ fontSize: 12, color: c.fg, flex: 1 }}>
                          <strong>{a.embarcacion}</strong> · {a.fecha} · {a.tipo_operacion}:
                          real {a.galones_reales} gal vs estimado {a.galones_estimados} gal
                        </Typography>
                      </Box>
                    );
                  })}
                  {anomalias.anomalias_consumo.length > 5 && (
                    <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                      + {anomalias.anomalias_consumo.length - 5} más…
                    </Typography>
                  )}
                </Box>
              )}
              {anomalias.anomalias_precio?.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626', mb: 0.5 }}>
                    Precios anómalos ({anomalias.anomalias_precio.length})
                  </Typography>
                  {anomalias.anomalias_precio.slice(0, 5).map(a => {
                    const c = NIVEL_COLOR[a.nivel] || NIVEL_COLOR.amarillo;
                    return (
                      <Box key={a.carga_id} sx={{
                        p: 1, mb: 0.5, borderRadius: 1, bgcolor: c.bg, border: `1px solid ${c.border}`,
                        display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                      }}>
                        <Chip label={`${a.desviacion_pct > 0 ? '+' : ''}${a.desviacion_pct}%`} size="small"
                          sx={{ bgcolor: c.border, color: '#fff', fontWeight: 700, fontSize: 11 }} />
                        <Typography sx={{ fontSize: 12, color: c.fg, flex: 1 }}>
                          Carga #{a.carga_id}: ${Number(a.precio_por_galon).toLocaleString('es-CO')}/gal
                          vs promedio ${Number(a.precio_promedio).toLocaleString('es-CO')}/gal
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}

          {/* Resumen por embarcación */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {(data.embarcaciones || []).map(e => (
              <Grid item xs={12} sm={6} md={4} key={e.embarcacion_id}>
                <Paper sx={{ p: 2, borderLeft: '4px solid #dc2626' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <DirectionsBoatIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                      {e.embarcacion_nombre}
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                        Consumo total
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>
                        {e.total_consumo.toLocaleString('es-CO', { maximumFractionDigits: 2 })} gal
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                        Cargado
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>
                        {e.total_cargado.toLocaleString('es-CO', { maximumFractionDigits: 2 })} gal
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 1 }}>
                    {e.dias_con_lecturas_completas} de {e.dias_totales} días con lecturas AM+PM
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Tabla detallada por día por embarcación */}
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Embarcación</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Día</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Nivel AM</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Cargas</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Nivel PM</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Consumo real</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.embarcaciones || []).flatMap(e =>
                    (e.por_dia || []).map(d => (
                      <TableRow key={`${e.embarcacion_id}-${d.fecha}`} hover>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{e.embarcacion_nombre}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{fmtDate(d.fecha)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          {d.nivel_am != null ? `${Number(d.nivel_am).toFixed(2)} gal` : (
                            <Chip label="Falta" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, color: '#16a34a', fontWeight: d.cargas_galones > 0 ? 700 : 400 }}>
                          {d.cargas_galones > 0 ? `+${d.cargas_galones.toFixed(2)}` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          {d.nivel_pm != null ? `${Number(d.nivel_pm).toFixed(2)} gal` : (
                            <Chip label="Falta" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: d.consumo_real != null ? '#dc2626' : '#94a3b8' }}>
                          {d.consumo_real != null ? `${d.consumo_real.toFixed(2)} gal` : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
